import { CalendarDate, DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { isEmpty } from 'lodash/fp';
import { DateTime } from 'luxon';
import { FC } from 'react';
import styled from 'styled-components';

import HoverCopyableField from 'dashboard/components/HoverCopyableField';
import HttpMethodSticker from 'dashboard/components/HttpMethodSticker';
import HttpStatusSticker from 'dashboard/components/HttpResponseSticker';
import Pagination from 'dashboard/components/Pagination';
import { MonoSpan } from 'dashboard/components/Typography';
import { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import TableControls, { FilterConfig } from 'dashboard/components-lib/TableControls';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import TableLayout, {
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import { UseTableViewsState } from 'dashboard/lib/hooks/useTableViews';
import { ApiKey } from 'dashboard/models/api-key';
import { ApiLog, ApiLogAttributes } from 'dashboard/models/api-log';
import { ApiLogsAttributeFilters } from 'dashboard/models/table-view';
import { ApiLogResponseStatus, ApiLogsFilterParams } from 'dashboard/redux/slices/api-logs';
import Card from 'design-system/components/Card';
import { DATE_AND_TIME_MILLIS_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import usePermission from 'lib/hooks/usePermission';
import { PaginationParams } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

const STATUS_FILTER_OPTIONS = [
  {
    label: 'Success',
    value: ApiLogResponseStatus.Success,
  },
  {
    label: 'Error',
    value: ApiLogResponseStatus.Error,
  },
];

const METHOD_FILTER_OPTIONS = [
  {
    label: 'GET',
    value: 'GET',
  },
  {
    label: 'POST',
    value: 'POST',
  },
  {
    label: 'PATCH',
    value: 'PATCH',
  },
  {
    label: 'DELETE',
    value: 'DELETE',
  },
];

const StyledMonoSpan = styled(MonoSpan)`
  display: flex;
  align-items: center;
  column-gap: 8px;
`;

interface DashboardApiLogsViewProps {
  apiLogs: ApiLog[];
  apiKeys: ApiKey[];
  isLoading: boolean;
  pageSize: number;
  paginationParams: PaginationParams;
  handlePageSizeClick: (pageSize: number) => void;
  handlePageLinkClick: (pageNumer: number) => void;
  filters: ApiLogsFilterParams;
  handleDateUpdate: (range: RangeValue<DateValue> | null) => void;
  handleOmniSearch: (omniQuery: string) => void;
  handleStatusFilterUpdate: (status: ApiLogResponseStatus[]) => void;
  handleMethodsFilterUpdate: (methods: ApiLogAttributes['request']['method'][]) => void;
  handleApiKeyFilterUpdate: (apiKeyIds: string[]) => void;
  tableViewsState: UseTableViewsState<ApiLogsAttributeFilters>;
}

const DashboardApiLogsView: FC<DashboardApiLogsViewProps> = ({
  apiLogs,
  apiKeys,
  isLoading,
  pageSize,
  paginationParams,
  handlePageSizeClick,
  handlePageLinkClick,
  filters,
  handleDateUpdate,
  handleOmniSearch,
  handleStatusFilterUpdate,
  handleMethodsFilterUpdate,
  handleApiKeyFilterUpdate,
  tableViewsState,
}) => {
  const { createdAtStart, createdAtEnd, omni, status, methods, apiKeyIds } = filters;

  const hasUsedFilters = !Object.values(filters).every(isEmpty);

  const statusFilterLabelTextMapper = useLabelTextMapper(STATUS_FILTER_OPTIONS, 'Statuses', {
    emptyIsAll: true,
  });

  const methodFilterLabelTextMapper = useLabelTextMapper(METHOD_FILTER_OPTIONS, 'Methods', {
    emptyIsAll: true,
  });

  const API_KEY_FILTER_OPTIONS = apiKeys.map((apiKey) => ({
    label: apiKey.attributes.name,
    value: apiKey.id,
  }));
  const apiKeyFilterLabelTextMapper = useLabelTextMapper(API_KEY_FILTER_OPTIONS, 'API Keys', {
    emptyIsAll: true,
  });
  const twoWeeksAgo = DateTime.local().minus({ weeks: 2 });

  const hasApiKeyViewPermission = usePermission(Permission.ApiKeyView);

  const filterConfig: FilterConfig[] = [
    {
      id: 'requestMethods',
      name: 'Request methods',
      type: 'multiselect-legacy',
      selectOptionValues: METHOD_FILTER_OPTIONS,
      labelTextMapper: methodFilterLabelTextMapper,
      value: methods || [],
      onChange: handleMethodsFilterUpdate,
    },
    {
      id: 'dateRange',
      name: `Dates (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`,
      type: 'daterange',
      onChange: handleDateUpdate,
      value: {
        startDate: createdAtStart,
        endDate: createdAtEnd,
      },
      datePickerProps: {
        granularity: 'minute',
        minDate: new CalendarDate(twoWeeksAgo.year, twoWeeksAgo.month, twoWeeksAgo.day),
      },
    },
    {
      id: 'status',
      name: 'Status',
      type: 'multiselect-legacy',
      selectOptionValues: STATUS_FILTER_OPTIONS,
      labelTextMapper: statusFilterLabelTextMapper,
      value: status || [],
      onChange: handleStatusFilterUpdate,
    },
    {
      id: 'apiKey',
      name: 'API Key',
      type: 'multiselect-legacy',
      selectOptionValues: API_KEY_FILTER_OPTIONS,
      labelTextMapper: apiKeyFilterLabelTextMapper,
      value: apiKeyIds || [],
      onChange: handleApiKeyFilterUpdate,
      enabled: hasApiKeyViewPermission,
    },
  ];

  return (
    <>
      <Container>
        <Header title='API Logs' />
        <Section>
          <Card>
            <Card.Body>
              <Card.Title>API logs</Card.Title>
              <TableControls
                showViews={tableViewsState.canViewTableViews}
                canEditViews={tableViewsState.canWriteTableViews}
                views={tableViewsState.tableViews.map((v) => ({
                  id: v.id,
                  name: v.attributes.name,
                }))}
                currentView={tableViewsState.currentTableView?.id}
                onSelectView={tableViewsState.handleSetCurrentTableView}
                onAddView={async () => {
                  return tableViewsState.handleCreateTableView('New View', {
                    columns: [],
                    filters,
                  });
                }}
                onViewNameChange={tableViewsState.handleSaveTableView}
                onDeleteView={tableViewsState.handleArchiveTableView}
                onDuplicateView={tableViewsState.handleDuplicateTableView}
                onSaveView={async () => {
                  if (tableViewsState.currentTableView) {
                    return tableViewsState.handleSaveTableView(
                      tableViewsState.currentTableView.id,
                      tableViewsState.currentTableView.attributes.name,
                      { columns: [], filters },
                    );
                  }
                }}
                isViewDirty={tableViewsState.isDirty}
                searchProps={{
                  id: 'api-logs-table__search',
                  handleInput: handleOmniSearch,
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  value: omni,
                  placeholder:
                    'API Log (req_), object ID (i.e. inq_, ver_), or path (i.e. /api/v1/inquiries)',
                }}
                filters={filterConfig}
              />
              <TableLayout
                dataTest='api-logs-view__api-logs-table'
                isLoading={isLoading}
                isEmpty={apiLogs.length === 0}
                showHeader={hasUsedFilters}
                headerTR={
                  <>
                    <TableTH>Request</TableTH>
                    <TableTH>Status</TableTH>
                    <TableTH>API log ID</TableTH>
                    <TableTH>{`Created at (${DateTime.local().toFormat(
                      TIMEZONE_NAME_ABBREVIATED,
                    )})`}</TableTH>
                    <TableTH>Error Summary</TableTH>
                  </>
                }
                nullState={
                  <TableNullState dataTestBlock='api-logs-view' hasUsedFilters={hasUsedFilters}>
                    No API logs have been created yet.
                  </TableNullState>
                }
              >
                {apiLogs.map((apiLog) => (
                  <TableLinkTR
                    to={`/dashboard/api-logs/${apiLog.id}`}
                    key={apiLog.id}
                    data-test={`api-log-table-row__${apiLog.id}`}
                    id='api-log-table__row'
                  >
                    <TableTD>
                      <StyledMonoSpan>
                        <HttpMethodSticker method={apiLog.attributes.request.method} />{' '}
                        {apiLog.attributes.request.path}
                      </StyledMonoSpan>
                    </TableTD>
                    <TableTD>
                      <HttpStatusSticker status={apiLog.attributes.response.status} />
                    </TableTD>
                    <TableTD>
                      <HoverCopyableField
                        copyValue={apiLog.id}
                        confirmationText='Copied API log ID'
                      >
                        <MonoSpan>{apiLog.id}</MonoSpan>
                      </HoverCopyableField>
                    </TableTD>
                    <TableTD>
                      {DateTime.fromISO(apiLog.attributes['created-at']).toFormat(
                        DATE_AND_TIME_MILLIS_FORMAT,
                      )}
                    </TableTD>
                    <TableTD>
                      <MonoSpan>{apiLog.attributes.response['error-summary']}</MonoSpan>
                    </TableTD>
                  </TableLinkTR>
                ))}
              </TableLayout>
              <Pagination
                pageSize={pageSize}
                paginationParams={paginationParams}
                handlePageSizeClick={handlePageSizeClick}
                handlePageLinkClick={handlePageLinkClick}
              />
            </Card.Body>
          </Card>
        </Section>
      </Container>
    </>
  );
};

export default DashboardApiLogsView;
